import { useState, type SyntheticEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router";

import { Button, PasswordField, TextField } from "../../components/ui/Controls";
import { AlertBanner } from "../../components/ui/Feedback";
import { ApiError } from "../../lib/api";
import { apiErrorMessage, useAuth } from "../../lib/auth";
import { usePageTitle } from "../../route-meta";

export function LoginPage() {
  const auth = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string>();
  const [rateLimit, setRateLimit] = useState<string>();
  const [busy, setBusy] = useState(false);
  usePageTitle("Giriş");
  if (auth.identity !== undefined) return <Navigate replace to="/genel-bakis" />;
  const setupComplete =
    (location.state as { readonly setupComplete?: boolean } | null)?.setupComplete === true;
  const submit = async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    setRateLimit(undefined);
    try {
      const result = await auth.login({ email, password });
      setPassword("");
      if (result.status === "mfa_required") void navigate("/giris/iki-adim");
      else void navigate("/genel-bakis", { replace: true });
    } catch (caught) {
      setPassword("");
      if (caught instanceof ApiError && caught.problem.code === "auth.rate_limited")
        setRateLimit(
          `Çok fazla deneme yapıldı. ${String(caught.problem.retryAfterSeconds ?? 60)} saniye sonra yeniden deneyin.`,
        );
      else setError(apiErrorMessage(caught));
    } finally {
      setBusy(false);
    }
  };
  return (
    <main className="auth-layout" id="main-content" tabIndex={-1}>
      <a className="skip-link" href="#main-content">
        Ana içeriğe geç
      </a>
      <section className="card auth-card stack">
        <h1>Giriş</h1>
        {setupComplete ? (
          <AlertBanner heading="Kurulum tamamlandı" tone="info">
            <p>Kurulum daha önce tamamlandı. Devam etmek için giriş yapın.</p>
          </AlertBanner>
        ) : null}
        {auth.sessionEnded ? (
          <AlertBanner heading="Oturum sona erdi" tone="info">
            <p>Oturumunuz sona erdi. Devam etmek için yeniden giriş yapın.</p>
          </AlertBanner>
        ) : null}
        <form
          className="form-stack"
          onSubmit={(event) => {
            void submit(event);
          }}
        >
          <TextField
            autoComplete="email"
            label="E-posta"
            onChange={(event) => {
              setEmail(event.currentTarget.value);
            }}
            required
            type="email"
            value={email}
          />
          <PasswordField
            autoComplete="current-password"
            label="Parola"
            onChange={(event) => {
              setPassword(event.currentTarget.value);
            }}
            required
            value={password}
          />
          {error === undefined ? null : (
            <AlertBanner heading="Giriş yapılamadı" tone="error">
              <p>{error}</p>
            </AlertBanner>
          )}
          {rateLimit === undefined ? null : (
            <AlertBanner heading="Bir süre bekleyin" tone="warning">
              <p>{rateLimit}</p>
            </AlertBanner>
          )}
          <Button busy={busy} busyLabel="Giriş yapılıyor…" type="submit">
            Giriş yap
          </Button>
        </form>
      </section>
    </main>
  );
}
